
import React, { useEffect, useRef } from 'react';

const FlowBackground: React.FC = () => {
  const backgroundRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const handleScroll = () => {
      if (!backgroundRef.current) return;
      
      const scrollY = window.scrollY;
      // Move nodes and connections based on scroll position
      const nodes = backgroundRef.current.querySelectorAll('.flow-node');
      const connections = backgroundRef.current.querySelectorAll('.flow-connection');
      
      nodes.forEach((node, index) => {
        const element = node as HTMLElement;
        // Different speeds for different nodes creates parallax effect
        const speed = 0.2 + (index % 3) * 0.05;
        element.style.transform = `translateY(${scrollY * speed}px)`;
      });
      
      connections.forEach((connection, index) => {
        const element = connection as HTMLElement;
        const speed = 0.15 + (index % 2) * 0.05;
        element.style.transform = `translateY(${scrollY * speed}px) scaleY(${1 + scrollY * 0.0005})`;
      });
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  return (
    <div 
      ref={backgroundRef}
      className="fixed inset-0 -z-10 overflow-hidden opacity-10"
      aria-hidden="true"
    >
      {/* Generate multiple flow nodes */}
      {Array.from({ length: 15 }).map((_, i) => (
        <div 
          key={`node-${i}`}
          className={`flow-node absolute rounded-lg shadow-md w-20 h-20 bg-noordpink-${i % 2 ? 'light' : 'DEFAULT'}`}
          style={{
            left: `${10 + (i % 5) * 20}%`,
            top: `${5 + Math.floor(i / 5) * 30}%`,
          }}
        >
          {/* Simulated content inside node */}
          <div className="w-full h-full flex items-center justify-center">
            <div className={`w-8 h-8 rounded-full bg-white ${i % 3 === 0 ? 'animate-pulse' : ''}`}></div>
          </div>
        </div>
      ))}
      
      {/* Generate connections between nodes */}
      {Array.from({ length: 10 }).map((_, i) => (
        <div 
          key={`connection-${i}`}
          className="flow-connection absolute bg-noordpink w-1 origin-top"
          style={{
            left: `${20 + (i % 4) * 20}%`,
            top: `${15 + Math.floor(i / 4) * 30}%`,
            height: '15%',
            opacity: 0.6,
          }}
        />
      ))}
      
      {/* Add some horizontal connections */}
      {Array.from({ length: 6 }).map((_, i) => (
        <div 
          key={`h-connection-${i}`}
          className="flow-connection absolute bg-noordpink h-1 origin-left"
          style={{
            left: `${15 + (i % 3) * 20}%`,
            top: `${20 + Math.floor(i / 3) * 30}%`,
            width: '15%',
            opacity: 0.6,
          }}
        />
      ))}
      
      {/* Processing indicators */}
      {Array.from({ length: 8 }).map((_, i) => (
        <div 
          key={`process-${i}`}
          className="absolute rounded-full bg-noordpink-dark"
          style={{
            left: `${20 + (i % 4) * 20}%`,
            top: `${20 + Math.floor(i / 4) * 30}%`,
            width: '8px',
            height: '8px',
            animation: `flowProcess ${1 + i % 3}s infinite`,
            animationDelay: `${i * 0.2}s`,
          }}
        />
      ))}
    </div>
  );
};

export default FlowBackground;
